import * as React from 'react'
import {FC} from 'react'
import {observer} from 'mobx-react'
import cn from 'classnames'
import {remove, indexOf} from 'lodash'
import {
  EpikQueryParam,
  isCreditCardPaymentMethod,
  PaymentMethod,
} from '@raisenow/epik'
import {useStores} from '../../../../lib/MobXProvider'
import {WidgetApi} from '../../../../lib/WidgetApi'
import {
  filterOutRedundantPostFinancePaymentMethods,
  filterOutSpecificCreditCardPaymentMethods,
  getPaymentMethodTitle,
} from '../../../../lib/helpers'
import {RippleWrapper} from '../../../wrappers/RippleWrapper'
import {RovingTabIndexWrapper} from '../../../wrappers/RovingTabIndexWrapper'

///////////////////////////////////////////////////////////////////////////////

type SubscriptionPaymentMethodsProps = {
  topElementId?: string
}

type PaymentMethodListItemProps = {
  topElementId?: string
  paymentMethod: PaymentMethod
  isCurrent?: boolean
}

///////////////////////////////////////////////////////////////////////////////

const PaymentMethodListItem: FC<PaymentMethodListItemProps> = observer(
  (props) => {
    const {api} = useStores<{api: WidgetApi}>()
    const {topElementId, paymentMethod, isCurrent} = props
    const {uiTransitionTimeout: timeout} = api.computedConfig

    let form
    let cardno

    if (api.subscriptionInfo) {
      form = api.subscriptionRenewForm
      cardno = api.subscriptionInfo.masked_cc
    }

    if (api.epmsSubscriptionInfo) {
      form = api.paymentForm
      cardno = api.epmsPaymentSourceInfo?.metadata?.find(
        (v) =>
          v.type === 'request' &&
          v.group === 'payment_info' &&
          v.name === 'number',
      )?.value

      if (cardno) {
        cardno = `* ${cardno}`
      }
    }

    const setPaymentMethod = (value) => {
      setTimeout(async () => {
        value = isCreditCardPaymentMethod(value)
          ? PaymentMethod.CREDIT_CARD
          : value
        form.setPaymentMethod(value)
        await api.ui.scrollTo('#' + topElementId)
      }, timeout)
    }

    const paymentMethodTitle = getPaymentMethodTitle(
      paymentMethod,
      api.subscriptionRenewForm,
      api.trans.bind(api),
    )

    return (
      <RippleWrapper>
        <button
          className={cn([
            'payment-method',
            paymentMethod,
            {current: isCurrent},
          ])}
          data-selectable={true}
          data-value={paymentMethod}
          role="option"
          aria-expanded={false}
          aria-controls={api.ui.generateUniqueElementId('payment-method-form')}
          onClick={() => setPaymentMethod(paymentMethod)}
        >
          <div
            className={cn([
              'image',
              `payment-method-bg-image-round-${paymentMethod}`,
            ])}
          />
          <div className="text">
            {paymentMethodTitle}
            {isCurrent &&
              isCreditCardPaymentMethod(paymentMethod) &&
              cardno && <span>, {cardno}</span>}
          </div>
        </button>
      </RippleWrapper>
    )
  },
)

///////////////////////////////////////////////////////////////////////////////

export const SubscriptionPaymentMethods: FC<SubscriptionPaymentMethodsProps> =
  observer((props) => {
    const {api} = useStores<{api: WidgetApi}>()
    const {topElementId} = props
    const {uiTransitionTimeout: timeout} = api.computedConfig

    let form
    let currentPaymentMethod: PaymentMethod | null = null

    if (api.subscriptionInfo) {
      form = api.subscriptionRenewForm
      currentPaymentMethod = api.subscriptionInfo.payment_method
    }

    if (api.epmsSubscriptionInfo) {
      form = api.paymentForm
      currentPaymentMethod = api.epmsPaymentSourceInfo
        .payment_method as PaymentMethod
    }

    let paymentMethods = api.subscriptionRenewForm.getPossiblePaymentMethods()
    paymentMethods = filterOutRedundantPostFinancePaymentMethods(paymentMethods)
    paymentMethods = filterOutSpecificCreditCardPaymentMethods(paymentMethods)

    const currentPaymentMethodIsCreditCard =
      isCreditCardPaymentMethod(currentPaymentMethod)

    // if current payment method is credit card, then filter out all credit card payment methods
    // else filter out current payment method
    remove(paymentMethods, (el) => {
      if (currentPaymentMethodIsCreditCard) {
        return isCreditCardPaymentMethod(el)
      }
      return el === currentPaymentMethod
    })

    if (currentPaymentMethodIsCreditCard) {
      currentPaymentMethod = PaymentMethod.CREDIT_CARD
    }

    const goBack = () => {
      setTimeout(async () => {
        form.setLastSelectedPaymentMethod(null)
        api.epik.updateCurrentUrl({
          params: {
            [EpikQueryParam.VIEW]: undefined,
          },
        })
        await api.ui.scrollToTop()
        api.ui.focusWidgetElementBySelector(
          '.payment-method-details .actions button',
        )
      }, timeout)
    }

    const lastPaymentMethod = form.getLastSelectedPaymentMethod()
    let currentIdx = 0

    if (lastPaymentMethod) {
      const idx = indexOf(paymentMethods, lastPaymentMethod)

      if (idx !== -1) {
        currentIdx = idx
      }

      api.ui.focusWidgetElementBySelector(
        `.subscription-payment-methods .payment-method.${lastPaymentMethod}`,
      )
    } else {
      api.ui.focusWidgetElementBySelector(
        '.subscription-payment-methods .payment-method.current',
      )
    }

    return (
      <div className="subscription-payment-methods">
        <div className="main">
          <div>
            {form.isPossiblePaymentMethod(currentPaymentMethod) && (
              <section className="update-current-payment-method">
                <div className="title">
                  <h2 className="text">
                    {api.trans(
                      'blocks.subscription_payment_method_update.payment_methods.update_current_payment_method.subtitle',
                    )}
                  </h2>
                </div>

                <fieldset>
                  <legend>
                    {api.trans(
                      'blocks.subscription_payment_method_update.payment_methods.update_current_payment_method.legend',
                    )}
                  </legend>

                  <div className="payment-methods">
                    <PaymentMethodListItem
                      isCurrent={true}
                      topElementId={topElementId}
                      paymentMethod={currentPaymentMethod}
                    />
                  </div>
                </fieldset>
              </section>
            )}

            {paymentMethods && paymentMethods.length > 0 && (
              <section className="choose-another-payment-method">
                <div className="title">
                  <h2 className="text">
                    {api.trans(
                      'blocks.subscription_payment_method_update.payment_methods.choose_another_payment_method.subtitle',
                    )}
                  </h2>
                </div>

                <fieldset>
                  <legend>
                    {api.trans(
                      'blocks.subscription_payment_method_update.payment_methods.choose_another_payment_method.legend',
                    )}
                  </legend>

                  <RovingTabIndexWrapper
                    currentIdx={currentIdx}
                    onSelect={form.setLastSelectedPaymentMethod.bind(form)}
                  >
                    <div className="payment-methods" role="listbox">
                      {paymentMethods.map((paymentMethod, key) => (
                        <PaymentMethodListItem
                          key={key}
                          topElementId={topElementId}
                          paymentMethod={paymentMethod}
                        />
                      ))}
                    </div>
                  </RovingTabIndexWrapper>
                </fieldset>
              </section>
            )}
          </div>
        </div>

        <div className="actions">
          <RippleWrapper>
            <button type="button" className="btn action-back" onClick={goBack}>
              <span className="btn-text">
                {api.trans(
                  'blocks.subscription_payment_method_update.payment_methods.cancel',
                )}
              </span>
            </button>
          </RippleWrapper>
        </div>
      </div>
    )
  })
